import React from 'react';
import { observer } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';

import view from 'modules/ticketPublicPage/stores/vouchers/view';
import form from 'modules/ticketPublicPage/stores/vouchers/form';
import config from '../../../config';

import {
  ORDERBIRD_PRIVACY_POLICY_URL,
  ORDERBIRD_TERMS_CONDITIONS_URL,
  RESMIO_PRIVACY_POLICY_URL,
  RESMIO_TERMS_CONDITIONS_B2C_URL,
} from 'app/constants';
import { colors } from 'app/variables';
import Button from 'app/commonComponents/Button';
import ResmioIcon from 'app/commonComponents/icons/ResmioIcon';
import OrderbirdIcon from 'app/commonComponents/icons/OrderbirdIcon';
import { Actions, PrivacyNotice } from './Action.styles';

const Action = observer(() => {
  const history = useHistory();
  const location = useLocation();

  const {
    showBackButton,
    onBackBtnClick,
    onNextBtnClick,
    step,
    disableSaveButton,
    PAYMENT_DETAILS,
    INITIAL,
    CONFIRMATION,
    WITHDRAWAL_SUMMARY_STEP,
    WITHDRAWAL_FINAL_STEP,
    WITHDRAWAL_SUCCESS_STEP,
    hideBuyButton,
    isSavingPayment,
    isWithdrawal,
    formValues,
  } = form;

  if (step === WITHDRAWAL_SUCCESS_STEP) {
    return null;
  }

  const { activeObject } = view;

  const withdrawalPurchase =
    formValues.get('withdrawal_voucher_purchase') || {};
  const canProceedWithdrawalSummary = Boolean(
    withdrawalPurchase.id &&
      !withdrawalPurchase.withdrawal_period_expired &&
      !withdrawalPurchase.withdrawal_cancellation_requested
  );
  const showDefaultNextButton =
    !hideBuyButton &&
    (isWithdrawal || activeObject.status === 'active') &&
    step !== WITHDRAWAL_FINAL_STEP &&
    (step !== WITHDRAWAL_SUMMARY_STEP || canProceedWithdrawalSummary);

  const handleCancelContractClick = () => {
    const refNum = formValues.get('ref_num');
    const email = formValues.get('email');
    const name = [formValues.get('name'), formValues.get('surname')]
      .filter(Boolean)
      .join(' ')
      .trim();

    const searchParams = new URLSearchParams();
    if (refNum) searchParams.set('ref_num', refNum);
    if (email) searchParams.set('email', email);
    if (name) searchParams.set('name', name);

    form.setFormDefaults();
    form.isWithdrawal = false;
    form.errors.clear();

    const currentPath = location.pathname;
    const withdrawalPath = `${currentPath.replace(/\/$/, '')}/withdrawal`;
    const query = searchParams.toString();

    history.push({
      pathname: withdrawalPath,
      search: query ? `?${query}` : '',
    });
  };

  const handleLogoClick = () => {
    const url = config.isOrderbirdFacility
      ? 'https://orderbird.com'
      : 'https://resmio.com';
    window.open(url);
  };

  const privacyText = i18n.sprintf(
    i18n.gettext(
      'By clicking the "Buy" button you agree to the <a target="_blank" href="%s" rel="nofollow">Terms & Conditions</a> and <a target="_blank" href="%s" rel="nofollow">Privacy Policy</a>'
    ),
    config.isOrderbirdFacility
      ? ORDERBIRD_TERMS_CONDITIONS_URL
      : RESMIO_TERMS_CONDITIONS_B2C_URL,
    config.isOrderbirdFacility
      ? ORDERBIRD_PRIVACY_POLICY_URL
      : RESMIO_PRIVACY_POLICY_URL
  );

  const cancellationPolicyUrl =
    activeObject.cancellation_policy_url_enabled &&
    activeObject.cancellation_policy_url;

  let withdrawalHtml = `
    <br /><br />
    <b>${i18n.gettext('Right of Withdrawal')}</b><br />
    ${i18n.gettext('You have the right to withdraw from this contract within 14 days without giving any reason. If vouchers have already been used, a fee may apply.')}
  `;

  if (cancellationPolicyUrl) {
    withdrawalHtml = `${withdrawalHtml}
      <a target="_blank" href="${cancellationPolicyUrl}" rel="nofollow">${i18n.gettext('Full Cancellation Policy')}</a>
    `;
  }

  return (
    <Actions>
      {step === PAYMENT_DETAILS ? (
        <PrivacyNotice
          dangerouslySetInnerHTML={{
            __html: privacyText + (withdrawalHtml || ''),
          }}
        />
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {showBackButton ? (
          <Button
            color={colors.pacificBlue}
            onClick={onBackBtnClick}
            disabled={isSavingPayment}
            hollow={true}
          >
            {i18n.gettext('Back')}
          </Button>
        ) : step === INITIAL && config.isOrderbirdFacility ? (
          <OrderbirdIcon
            onClick={handleLogoClick}
            style={{ width: '7rem', cursor: 'pointer' }}
            role="link"
            aria-label={i18n.gettext('Orderbird')}
          />
        ) : step !== CONFIRMATION ? (
          <ResmioIcon
            onClick={handleLogoClick}
            style={{ width: '7rem', cursor: 'pointer' }}
            role="link"
            aria-label={i18n.gettext('Resmio')}
          />
        ) : null}
        {showDefaultNextButton ? (
          <Button
            color={colors.pacificBlue}
            onClick={onNextBtnClick}
            disabled={disableSaveButton}
          >
            {step === PAYMENT_DETAILS
              ? i18n.gettext('Buy')
              : i18n.gettext('Next')}
          </Button>
        ) : null}
        {step === WITHDRAWAL_FINAL_STEP ? (
          <Button
            color={colors.amaranth}
            onClick={onNextBtnClick}
            disabled={disableSaveButton || isSavingPayment}
          >
            {i18n.gettext('Confirm cancellation')}
          </Button>
        ) : null}
        {step === CONFIRMATION ? (
          <div style={{ marginInline: '1rem' }}>
            <Button
              onClick={onNextBtnClick}
              color={colors.pacificBlue}
              style={{
                margin: '1rem auto',
                alignSelf: 'center',
                padding: '0.75rem 4rem',
                width: '100%',
              }}
            >
              {i18n.gettext('Buy another voucher')}
            </Button>
            <Button
              onClick={handleCancelContractClick}
              color={colors.amaranth}
              style={{
                marginBottom: '1rem',
                alignSelf: 'center',
                padding: '0.75rem 4rem',
                width: '100%',
              }}
            >
              {i18n.gettext('Cancel the contract')}
            </Button>
          </div>
        ) : null}
      </div>
    </Actions>
  );
});

export default Action;
